const { uaDataLayerCall } = require('./ua');

/**
 * Set a single or multiple key => value pairs on the data layer
 * - This function is overloaded and accepts two different sets of parameters
 *   depending on if you are setting a single attribute on the data layer or multiple attributes
 *
 * Optional options (second or third parameter as an object)
 * - Optionally scope the attribute(s) to a screen, case insensitive
 * - Optionally specify the persistency level:
 *      - "none" (default): no special treatment (goes away on page reload)
 *      - "visitor": Stored and reloaded until the internal zgAnonymousId changes
 *      - "forever": Stored and reloaded forever (or until user clears browser data)
 *
 * @note See README for function interface description.
 * @param {any} args either two parameters (string key, any value)
 *                   or a single object (keys mapped to values)
 *                   both interfaces can include a second or third parameter
 *                   for optional options
 * @param {Object} [options] optional options
 * @param {bool} [options.persistency] persistency level
 * @param {string} [options.screenName] scope the attribute to a screen, case insensitive
 * @returns {Promise<boolean>} resolves to true if set, false otherwise
 *                             rejects if data layer cannot be called
 */
function set(...args) {
    return uaDataLayerCall('set', ...args);
}

/**
 * Retrieve a key from the data layer
 * - Optionally pass in an array of keys, which will return all keys in a k=>v object
 *
 * @param {string|string[]} key data layer key(s)
 * @param {Object} [options] optional options
 * @param {string} [options.screenName] scope keys to a specific screen, case insensitive
 * @returns {Promise<any|{[key:string]:any}>} resolves to any including undefined if
 *                                            key does not exist
 *                                            rejects if data layer cannot be called
 */
function get(key, options) {
    return uaDataLayerCall('get', key, options);
}

/**
 * Retrieve all keys from the data layer, optionally scoped to a specific screen
 *
 * @param {Object} [options] optional options
 * @param {string} [options.screenName] scope keys to a specific screen, case insensitive
 * @returns {Promise<{[key:string]:any}>} resolves to an Object with keys mapped to value
 *                                        rejects if data layer cannot be called
 */
function getAll(options) {
    return uaDataLayerCall('getAll', options);
}

/**
 * Check to see if a key is on the data layer
 *
 * @param {string|string[]} key data layer key(s)
 * @param {Object} [options] optional options
 * @param {string} [options.screenName] scope keys to a specific screen, case insensitive
 * @returns {Promise<boolean|{[key:string]: boolean}>} resolves to true if the
 *          key exists, false otherwise. If multiple keys passed in, resolves
 *          to object of keys mapped to true or false
 *          rejects if data layer cannot be called
 */
function has(key, options) {
    return uaDataLayerCall('has', key, options);
}

/**
 * Remove a key from the data layer
 *
 * @param {string|string[]} key data layer key(s)
 * @param {Object} [options] optional options
 * @param {string} [options.screenName] scope keys to a specific screen, case insensitive
 * @returns {Promise<boolean>} resolves to true if no issues removing key(s), false otherwise
 *                             rejects if data layer cannot be called
 */
function remove(key, options) {
    return uaDataLayerCall('remove', key, options);
}

/**
 * Clear all attributes scoped to a particular screen
 *
 * @param {string} screenName screen to which keys are scoped to clear, case insensitive
 * @returns {Promise<boolean>} resolves to true if no problems clearing data layer, false otherwise
 *                             rejects if data layer cannot be called
 */
function clear(screenName) {
    return uaDataLayerCall('clear', screenName);
}

/**
 * Listen for an event to trigger on the data layer
 *
 * @param {string} eventName the event name to trigger on
 * @param {function} handler the function to trigger
 * @param {Object} [options] optional options
 * @param {bool} [options.once] only trigger once if true, default false
 * @returns {Promise<boolean>} resolves to true if handler is listening
 *                             successfully, false otherwise
 *                             rejects if data layer cannot be called
 */
function on(eventName, handler, options) {
    return uaDataLayerCall('on', eventName, handler, options);
}

/**
 * Remove a previously registered handler from triggering on a data layer event
 *
 * @param {string} eventName the event name to trigger on
 * @param {function} handler the function to trigger
 * @returns {Promise<boolean>} resolves to true if no problems removing listener, false otherwise
 *                             rejects if data layer cannot be called
 */
function off(eventName, handler) {
    return uaDataLayerCall('off', eventName, handler);
}

/**
 * Trigger a data layer event, causing any listeners to be triggered
 *
 * @param {string} eventName the event name to trigger
 * @param {Object} [eventData] an object containing event data to pass to listeners
 * @returns {Promise<boolean>} resolves to true once triggering is finished, false otherwise
 *                             rejects if data layer cannot be called
 */
function event(eventName, eventData) {
    return uaDataLayerCall('event', eventName, eventData);
}

module.exports = {
    set,
    get,
    getAll,
    has,
    remove,
    clear,
    on,
    off,
    event,
};
